/**
 * Row actions menu for a Rules table row.
 *
 * Desktop: dropdown anchored below the three-dot button.
 * Mobile: bottom sheet that slides up from the bottom with the
 * rule name as a title.
 *
 * Rendered as a React Portal into document.body so it floats
 * above the sticky header, the side panel, and any z-index
 * stacking context from the table.
 *
 * Position (desktop) is computed from the anchor element's
 * bounding rect at open time. Re-positions on window resize
 * while open.
 *
 * Six actions:
 *   - Edit rule          (stub toast in PR 9)
 *   - Duplicate          (stub toast in PR 9)
 *   - Pause / Resume     (flips rule.status via onTogglePause)
 *   - Apply to past      (opens ConfirmModal via onApplyToPast)
 *   - Move up / down     (via onMoveUp / onMoveDown)
 *   - Delete rule        (red, opens ConfirmModal via onDelete)
 *
 * Keyboard:
 *   - Escape         closes
 *   - ArrowUp/Down   navigates items
 *   - Enter          triggers focused item
 *   - E              Edit
 *   - D              Duplicate
 *
 * Closes on outside click, Escape, route change (handled by
 * parent unmounting the drawer).
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import {
  Edit2,
  Copy,
  Pause,
  Play,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  Trash2,
} from "lucide-react";
import type { Rule } from "../rulesTypes";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
  red: "#B42318",
  redWash: "#FEF3F2",
};

interface RowActionsMenuProps {
  isOpen: boolean;
  rule: Rule;
  anchorEl: HTMLElement | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onClose: () => void;
  onEdit: (rule: Rule) => void;
  onDuplicate: (rule: Rule) => void;
  onTogglePause: (rule: Rule) => void;
  onApplyToPast: (rule: Rule) => void;
  onMoveUp: (rule: Rule) => void;
  onMoveDown: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
}

export function RowActionsMenu({
  isOpen,
  rule,
  anchorEl,
  canMoveUp,
  canMoveDown,
  onClose,
  onEdit,
  onDuplicate,
  onTogglePause,
  onApplyToPast,
  onMoveUp,
  onMoveDown,
  onDelete,
}: RowActionsMenuProps) {
  const { isMobile } = useBreakpoint();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Compute desktop dropdown position from anchor's bounding rect.
  useLayoutEffect(() => {
    if (!isOpen || isMobile || !anchorEl) {
      setPosition(null);
      return;
    }
    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      const menuWidth = 240;
      let left = rect.right - menuWidth;
      if (left < 8) left = 8;
      const right = left + menuWidth;
      if (right > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setPosition({
        top: rect.bottom + 6,
        left,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isOpen, isMobile, anchorEl]);

  // Close on outside click, Escape, shortcut keys.
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key.toLowerCase() === "e" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onEdit(rule);
        onClose();
      } else if (e.key.toLowerCase() === "d" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onDuplicate(rule);
        onClose();
      }
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };

    window.addEventListener("keydown", handleKey);
    window.addEventListener("mousedown", handleClick);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("mousedown", handleClick);
    };
  }, [isOpen, anchorEl, rule, onClose, onEdit, onDuplicate]);

  if (!isOpen) return null;

  const items: MenuItemDef[] = [
    {
      label: "Edit rule",
      icon: Edit2,
      onClick: () => onEdit(rule),
      shortcut: "E",
    },
    {
      label: "Duplicate",
      icon: Copy,
      onClick: () => onDuplicate(rule),
      shortcut: "D",
    },
    {
      label: rule.status === "active" ? "Pause rule" : "Resume rule",
      icon: rule.status === "active" ? Pause : Play,
      onClick: () => onTogglePause(rule),
    },
    { divider: true },
    {
      label: "Apply to past transactions",
      icon: RotateCcw,
      onClick: () => onApplyToPast(rule),
    },
    { divider: true },
    {
      label: "Move up",
      icon: ChevronUp,
      onClick: () => onMoveUp(rule),
      disabled: !canMoveUp,
    },
    {
      label: "Move down",
      icon: ChevronDown,
      onClick: () => onMoveDown(rule),
      disabled: !canMoveDown,
    },
    { divider: true },
    {
      label: "Delete rule",
      icon: Trash2,
      onClick: () => onDelete(rule),
      destructive: true,
    },
  ];

  if (isMobile) {
    return createPortal(
      <>
        <div style={sheetOverlayStyle} onClick={onClose} aria-hidden="true" />
        <div
          ref={menuRef}
          style={sheetStyle}
          role="menu"
          aria-label={`Actions for ${rule.name}`}
        >
          <div style={sheetHandleStyle} />
          <div style={sheetTitleStyle}>{rule.name}</div>
          {items.map((item, idx) => (
            <MenuItem
              key={idx}
              item={item}
              variant="sheet"
              onClick={() => {
                if (!item.disabled && item.onClick) {
                  item.onClick();
                  onClose();
                }
              }}
            />
          ))}
        </div>
      </>,
      document.body,
    );
  }

  if (!position) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{ ...dropdownStyle, top: position.top, left: position.left }}
      role="menu"
      aria-label={`Actions for ${rule.name}`}
    >
      {items.map((item, idx) => (
        <MenuItem
          key={idx}
          item={item}
          variant="dropdown"
          onClick={() => {
            if (!item.disabled && item.onClick) {
              item.onClick();
              onClose();
            }
          }}
        />
      ))}
    </div>,
    document.body,
  );
}

interface MenuItemDef {
  label?: string;
  icon?: typeof Edit2;
  onClick?: () => void;
  shortcut?: string;
  destructive?: boolean;
  disabled?: boolean;
  divider?: boolean;
}

interface MenuItemProps {
  item: MenuItemDef;
  variant: "dropdown" | "sheet";
  onClick: () => void;
}

function MenuItem({ item, variant, onClick }: MenuItemProps) {
  if (item.divider) {
    return <div style={dividerStyle} />;
  }
  const Icon = item.icon;
  const isDestructive = item.destructive;
  const color = isDestructive ? PALETTE.red : PALETTE.ink3;
  const bgHover = isDestructive ? PALETTE.redWash : PALETTE.bgFade;

  const baseStyle: CSSProperties =
    variant === "sheet" ? sheetItemStyle : dropdownItemStyle;

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={item.disabled}
      style={{
        ...baseStyle,
        color: isDestructive ? PALETTE.red : PALETTE.ink,
        opacity: item.disabled ? 0.4 : 1,
        cursor: item.disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!item.disabled) e.currentTarget.style.background = bgHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {Icon && <Icon size={variant === "sheet" ? 18 : 15} color={color} />}
      <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
      {item.shortcut && variant === "dropdown" && (
        <span style={shortcutStyle}>{item.shortcut}</span>
      )}
    </button>
  );
}

const dropdownStyle: CSSProperties = {
  position: "fixed",
  width: 240,
  background: "#ffffff",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  boxShadow: "0 12px 32px -4px rgba(14, 26, 26, 0.18)",
  padding: "6px 0",
  zIndex: 2000,
};

const dropdownItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 500,
  background: "transparent",
  border: 0,
  width: "100%",
  fontFamily: "inherit",
  transition: "background 0.1s",
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: PALETTE.lineSoft,
  margin: "4px 0",
};

const shortcutStyle: CSSProperties = {
  marginLeft: "auto",
  fontSize: 11,
  color: PALETTE.ink4,
  fontFamily: "ui-monospace, Menlo, monospace",
};

const sheetOverlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
  zIndex: 1999,
};

const sheetStyle: CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  background: "#ffffff",
  borderRadius: "16px 16px 0 0",
  padding: "6px 0 20px",
  zIndex: 2000,
  boxShadow: "0 -8px 24px -4px rgba(14, 26, 26, 0.14)",
  maxHeight: "80vh",
  overflowY: "auto",
};

const sheetHandleStyle: CSSProperties = {
  width: 36,
  height: 4,
  background: PALETTE.line,
  borderRadius: 2,
  margin: "10px auto 14px",
};

const sheetTitleStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: PALETTE.ink4,
  fontWeight: 700,
  padding: "0 20px 10px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  marginBottom: 6,
};

const sheetItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 20px",
  fontSize: 14,
  fontWeight: 500,
  background: "transparent",
  border: 0,
  width: "100%",
  fontFamily: "inherit",
  textAlign: "left",
  transition: "background 0.1s",
};